import { useMemo, useState } from "react";
import { Card } from "@/components/layout";
import {
  MetricKpiTile,
  PanelError,
  PanelLoading,
  SectionHeading,
  panelErrorTitle,
} from "@/components/dashboard";
import { DonutChart, QmFunnelBars } from "@/components/charts";
import type { DonutSlice, DonutTone } from "@/components/charts";
import type { TimeRange } from "@/lib/time-range";
import { useQmFunnel } from "@/hooks/useQmFunnel";
import type { QmFunnelType } from "@/lib/validation";

interface QmFunnelSectionProps {
  range: TimeRange;
}

const FUNNEL_OPTIONS: ReadonlyArray<{ value: QmFunnelType; label: string }> = [
  { value: "reservation", label: "Reservation" },
  { value: "quote", label: "Quote" },
];

const DROPOFF_TONES: readonly DonutTone[] = ["red", "amber", "sky", "slate"];

export function QmFunnelSection({ range }: QmFunnelSectionProps) {
  const [funnelType, setFunnelType] = useState<QmFunnelType>("reservation");
  const [selectedStageKey, setSelectedStageKey] = useState<string | null>(null);

  const qm = useQmFunnel({ from: range.from, to: range.to, funnel: funnelType });
  const err = qm.error ? panelErrorTitle(qm.error) : undefined;

  const stages = useMemo(() => qm.data?.stages ?? [], [qm.data]);

  const biggestDropStage = useMemo(() => {
    if (stages.length === 0) return undefined;
    return stages.reduce((worst, s) => (s.dropOff > worst.dropOff ? s : worst));
  }, [stages]);

  const donutSlices = useMemo<DonutSlice[]>(() => {
    if (!qm.data) return [];
    const dropSlices = stages
      .filter((s) => s.dropOff > 0)
      .map((s, idx) => ({
        label: `Left at ${s.label}`,
        value: s.dropOff,
        tone: DROPOFF_TONES[idx % DROPOFF_TONES.length],
      }));
    return [
      ...dropSlices,
      { label: "Completed", value: qm.data.completed, tone: "emerald" as DonutTone },
    ];
  }, [qm.data, stages]);

  const selectedStage = selectedStageKey
    ? stages.find((s) => s.key === selectedStageKey)
    : undefined;

  return (
    <section className="flex flex-col gap-3">
      <SectionHeading
        title="Reservation Funnel"
        frs={["A2"]}
        source="bigquery"
        description="QuantumMetric page-path funnel with corrected sequential counts (replaces the legacy >100% funnel). Each stage links to replays of the sessions that dropped off."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricKpiTile
          label="Entered"
          value={qm.data ? qm.data.entered.toLocaleString("en-US") : "—"}
          delta={qm.data ? `${qm.data.label} funnel` : undefined}
          tone="sky"
          hint="Sessions reaching the first stage of the funnel — the top-of-funnel population."
          isLoading={qm.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Completed"
          value={qm.data ? qm.data.completed.toLocaleString("en-US") : "—"}
          delta="Reached the final stage"
          tone="emerald"
          hint="Sessions that reached the final funnel stage in sequence."
          isLoading={qm.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Overall conversion"
          value={qm.data ? `${qm.data.overallConversionPct.toFixed(1)}%` : "—"}
          delta="Completed / entered"
          tone="sky"
          hint="Share of entered sessions that reached the final stage. The single source-of-truth conversion number for this funnel."
          isLoading={qm.isLoading}
          errorTitle={err}
        />
        <MetricKpiTile
          label="Biggest drop-off"
          value={
            biggestDropStage
              ? biggestDropStage.dropOff.toLocaleString("en-US")
              : "—"
          }
          delta={biggestDropStage?.label}
          tone="red"
          hint="The stage where the most sessions left the funnel. Start the drop-off replay drill-down here."
          isLoading={qm.isLoading}
          errorTitle={err}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card
          title="Sequential funnel & drop-off"
          className="lg:col-span-2"
          action={
            <div
              className="flex items-center gap-1"
              role="group"
              aria-label="Funnel type"
            >
              {FUNNEL_OPTIONS.map((opt) => {
                const active = opt.value === funnelType;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setFunnelType(opt.value);
                      setSelectedStageKey(null);
                    }}
                    aria-pressed={active}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 transition-colors ${
                      active
                        ? "bg-info/15 text-info-text ring-info/30"
                        : "text-fg-muted ring-border-subtle hover:bg-surface-raised/60"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          }
        >
          {qm.isLoading ? (
            <PanelLoading height={220} />
          ) : qm.error ? (
            <PanelError error={qm.error} onRetry={() => qm.refetch()} />
          ) : (
            <QmFunnelBars
              stages={stages}
              entered={qm.data?.entered ?? 0}
              selectedKey={selectedStageKey}
              onSelectStage={setSelectedStageKey}
            />
          )}
        </Card>

        <Card title="Where sessions ended">
          {qm.isLoading ? (
            <PanelLoading height={220} />
          ) : qm.error ? (
            <PanelError error={qm.error} onRetry={() => qm.refetch()} />
          ) : (
            <DonutChart
              slices={donutSlices}
              centerLabel={
                qm.data
                  ? `${qm.data.entered.toLocaleString("en-US")} entered`
                  : undefined
              }
              emptyMessage="No funnel data in the selected range"
            />
          )}
        </Card>
      </div>

      {selectedStage && (
        <Card title={`Drop-off replays — ${selectedStage.label}`}>
          {selectedStage.replayLinks.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {selectedStage.replayLinks.map((link, i) => (
                <li key={link}>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-inset/60 px-3 py-1.5 text-xs text-info-text transition-colors hover:bg-surface-raised/60"
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full bg-info/70"
                      aria-hidden="true"
                    />
                    Session {i + 1}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-fg-faint">
                {selectedStage.dropOff.toLocaleString("en-US")} sessions dropped off
                here. Replay deep-links unavailable (QM_REPLAY_BASE_URL not configured
                on the backend).
              </p>
              {selectedStage.dropOffSessionIds.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {selectedStage.dropOffSessionIds.map((id) => (
                    <li
                      key={id}
                      className="rounded border border-border-subtle bg-surface-inset/60 px-2 py-0.5 font-mono text-[11px] text-fg-disabled"
                    >
                      {id}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </Card>
      )}
    </section>
  );
}
